/* Deterministic shuffling for Endless Mode.
 *
 * ── Why seeded, and not just Math.random() ──
 * Play progress lives in the URL hash as `#<index>-<flags>` — one character per item. That is
 * what survives a refresh, a Back out of an article, and a bfcache restore, and it is an INDEX
 * into the item list. So if the order were re-randomised on every load, coming back to index 7
 * would land on a DIFFERENT headline than the one he just judged, and the verdicts recorded in
 * the flags would silently belong to the wrong headlines. On camera that is unrecoverable.
 *
 * The seed is minted once, written into the URL as ?s=<seed>, and the order is derived from it.
 * The order then survives exactly as far as the progress does, because both live in the URL.
 */

/** Inclusive upper bound for a minted seed. Well inside 2^32, so the PRNG stays in integer range. */
const SEED_MAX = 0xffffffff;

/**
 * mulberry32 — small, fast, and good enough for shuffling a few hundred headlines. It is a
 * *reproducible* generator, which is the whole requirement here; it is not for anything
 * security-sensitive and is not used for tokens (see mintToken in shareApi.js for that).
 */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * A copy of `items` in a shuffled order that depends only on `seed`.
 * The same seed always gives the same permutation; the input array is never mutated.
 */
export function shuffleWithSeed<T>(items: readonly T[], seed: number): T[] {
  const out = [...items];
  // A non-finite seed would make the PRNG return NaN and Fisher-Yates would quietly no-op,
  // which looks like "shuffle is broken" rather than "the seed was bad".
  const safeSeed = Number.isFinite(seed) ? Math.abs(Math.trunc(seed)) : 1;
  const random = mulberry32(safeSeed || 1);
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/** A fresh seed for a new session. */
export function mintSeed(): number {
  // crypto is available everywhere this runs; the fallback is only so a test environment
  // without it does not throw.
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const arr = new Uint32Array(1);
    crypto.getRandomValues(arr);
    return arr[0] || 1;
  }
  return Math.floor(Math.random() * SEED_MAX) + 1;
}

/** The seed already in the URL, or null when there is none to reuse. */
export function readSeed(search: string): number | null {
  const raw = new URLSearchParams(search).get('s');
  if (!raw) return null;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * The seed for this session: whatever the URL already carries, or a new one written into the
 * URL so a refresh reuses it. replaceState, not push — the seed is not a place in history.
 */
export function ensureSeed(): number {
  const existing = readSeed(window.location.search);
  if (existing !== null) return existing;

  const seed = mintSeed();
  const url = new URL(window.location.href);
  url.searchParams.set('s', String(seed));
  // Keep the hash: it carries play progress, and dropping it here would reset the run.
  window.history.replaceState(null, '', `${url.pathname}${url.search}${window.location.hash}`);
  return seed;
}
